import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  PendingActions,
  Undo
} from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';
import SignatureField from '../../camo/SignatureField';
import { ROLES } from '../../../constants/roles';

/**
 * Tab component for displaying and completing AMP / work-order checklist items
 */
const AMPChecksTab = ({ workOrder, onUpdate, user, title = 'Checklist' }) => {
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completingCheck, setCompletingCheck] = useState(null);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoingCheck, setUndoingCheck] = useState(null);
  const [undoReason, setUndoReason] = useState('');
  const [formData, setFormData] = useState({
    techSignature: null,
    techSignatureType: 'NAME',
    ameSignature: null,
    ameSignatureType: 'NAME',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  if (!workOrder || !workOrder.ampChecks || workOrder.ampChecks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          This work order does not have any checklist items.
        </Typography>
      </Box>
    );
  }

  const completedCount = workOrder.ampChecks.filter(check => check.status === 'COMPLETED').length;
  const totalCount = workOrder.ampChecks.length;
  const progress = (completedCount / totalCount) * 100;

  const handleComplete = (check) => {
    setCompletingCheck(check);
    setFormData({
      techSignature: check.techSignature?.signatureValue || null,
      techSignatureType: check.techSignature?.signatureType || 'NAME',
      ameSignature: check.ameSignature?.signatureValue || null,
      ameSignatureType: check.ameSignature?.signatureType || 'NAME',
      remarks: check.remarks || ''
    });
    setCompleteDialogOpen(true);
  };

  const handleSaveComplete = async () => {
    if (!formData.techSignature || !formData.ameSignature) {
      showToast('Both Tech Signature and AME Signature are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const signedBy = user?.id || user?._id;
      await axios.put(`/api/maintenance/${workOrder._id}/amp-checks/${completingCheck._id}/complete`, {
        techSignature: {
          signatureType: formData.techSignatureType,
          signatureValue: formData.techSignature,
          signedBy,
          signedAt: new Date()
        },
        ameSignature: {
          signatureType: formData.ameSignatureType,
          signatureValue: formData.ameSignature,
          signedBy,
          signedAt: new Date()
        },
        remarks: formData.remarks
      });
      
      onUpdate();
      setCompleteDialogOpen(false);
      setCompletingCheck(null);
      showToast('Checklist item completed successfully', 'success');
    } catch (error) {
      console.error('Error completing AMP check:', error);
      showToast(error.response?.data?.message || 'Failed to complete checklist item', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUndo = (check) => {
    setUndoingCheck(check);
    setUndoReason('');
    setUndoDialogOpen(true);
  };

  const handleUndoComplete = async () => {
    if (!undoingCheck?._id) return;
    setLoading(true);
    try {
      await axios.put(`/api/maintenance/${workOrder._id}/amp-checks/${undoingCheck._id}/undo`, {
        reason: undoReason.trim() || undefined
      });
      onUpdate();
      setUndoDialogOpen(false);
      setUndoingCheck(null);
      setUndoReason('');
      showToast('Checklist item completion undone', 'success');
    } catch (error) {
      console.error('Error undoing AMP check:', error);
      showToast(error.response?.data?.message || 'Failed to undo checklist completion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      default:
        return 'default';
    }
  };

  const canComplete = user?.role === ROLES.MRO_ENGINEER && workOrder.status === 'IN_PROGRESS';
  const canUndoComplete = user?.role === ROLES.SUPER_ADMIN
    && !workOrder.mroQmCompletionAcknowledged;

  const displaySequence = (check) =>
    (check?.sequenceLabel && String(check.sequenceLabel).trim()) ? check.sequenceLabel : check?.sequenceNumber;

  return (
    <Box>
      {/* Progress Indicator */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            {title} progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {completedCount} of {totalCount} completed
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
        {completedCount < totalCount && (
          <Alert severity="info" sx={{ mt: 2 }}>
            All checklist items must be completed before the work order can be marked as complete.
          </Alert>
        )}
      </Box>

      {/* Checklist Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="80">S/N</TableCell>
              <TableCell width="100">S/N Label</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Task Reference ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Completed By</TableCell>
              <TableCell>Completed At</TableCell>
              <TableCell>Remarks</TableCell>
              <TableCell>Handover note</TableCell>
              <TableCell width="180">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workOrder.ampChecks.map((check, index) => (
              <TableRow key={check._id || index}>
                <TableCell>{check.sequenceNumber}</TableCell>
                <TableCell>{(check.sequenceLabel && String(check.sequenceLabel).trim()) ? check.sequenceLabel : '—'}</TableCell>
                <TableCell>{check.description}</TableCell>
                <TableCell>{check.taskReferenceId || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={check.status || 'PENDING'}
                    color={getStatusColor(check.status)}
                    size="small"
                    icon={check.status === 'COMPLETED' ? <CheckCircle /> : <PendingActions />}
                  />
                </TableCell>
                <TableCell>
                  {check.completedBy?.firstName && check.completedBy?.lastName
                    ? `${check.completedBy.firstName} ${check.completedBy.lastName}`
                    : '-'}
                </TableCell>
                <TableCell>
                  {check.completedAt ? new Date(check.completedAt).toLocaleString() : '-'}
                </TableCell>
                <TableCell>{check.remarks || '-'}</TableCell>
                <TableCell>
                  {check.lastHandoverNote ? (
                    <Box>
                      <Typography variant="body2">{check.lastHandoverNote}</Typography>
                      {check.lastHandedOverAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {new Date(check.lastHandedOverAt).toLocaleString()}
                          {check.lastHandedOverBy?.firstName
                            ? ` · from ${check.lastHandedOverBy.firstName}${check.lastHandedOverBy.lastName ? ` ${check.lastHandedOverBy.lastName}` : ''}`
                            : ''}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {check.status !== 'COMPLETED' && canComplete && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CheckCircle />}
                        onClick={() => handleComplete(check)}
                      >
                        Complete
                      </Button>
                    )}
                    {check.status === 'COMPLETED' && canUndoComplete && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<Undo />}
                        onClick={() => handleOpenUndo(check)}
                      >
                        Undo
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Complete Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Complete AMP Check</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Sequence (S/N) Label</Typography>
              <Typography variant="body1" gutterBottom>{displaySequence(completingCheck)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body1" gutterBottom>{completingCheck?.description}</Typography>
            </Box>
            {completingCheck?.taskReferenceId && (
              <Box>
                <Typography variant="body2" color="text.secondary">Task Reference ID</Typography>
                <Typography variant="body1" gutterBottom>{completingCheck.taskReferenceId}</Typography>
              </Box>
            )}
            <SignatureField
              label="Tech Signature *"
              value={formData.techSignature}
              signatureType={formData.techSignatureType}
              onChange={(value, type) => setFormData({
                ...formData,
                techSignature: value,
                techSignatureType: type
              })}
              entityId={workOrder._id}
              entityType="AMP_CHECK_TECH_SIGNATURE"
            />
            <SignatureField
              label="AME/MRO/App Person's Signature *"
              value={formData.ameSignature}
              signatureType={formData.ameSignatureType}
              onChange={(value, type) => setFormData({
                ...formData,
                ameSignature: value,
                ameSignatureType: type
              })}
              entityId={workOrder._id}
              entityType="AMP_CHECK_AME_SIGNATURE"
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks"
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Optional remarks..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveComplete}
            variant="contained"
            disabled={loading || !formData.techSignature || !formData.ameSignature}
          >
            {loading ? 'Completing...' : 'Complete Check'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo completion dialog (Super Admin) */}
      <Dialog
        open={undoDialogOpen}
        onClose={() => !loading && setUndoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Undo AMP check completion</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="warning">
              This clears signatures and completion for this checklist item. Only Super Admin can do this.
              {workOrder.mroEngineerCompleted
                ? ' MRO engineer completion on this work order will also be cleared so the checklist stays consistent.'
                : ''}
            </Alert>
            <Box>
              <Typography variant="body2" color="text.secondary">Sequence (S/N) Label</Typography>
              <Typography variant="body1">{displaySequence(undoingCheck)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body1">{undoingCheck?.description}</Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason (optional)"
              value={undoReason}
              onChange={(e) => setUndoReason(e.target.value)}
              placeholder="Why is this completion being undone?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUndoComplete}
            variant="contained"
            color="warning"
            disabled={loading}
            startIcon={<Undo />}
          >
            {loading ? 'Undoing...' : 'Undo completion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AMPChecksTab;
